/**
 * @file receipt-columns.tsx
 * @description Колонки реестра приёмок для `ResponsiveList` (phase19.31,
 * зона «Склад»; разбор B — «Приёмки»).
 *
 * Таблица от 1024: Номер · Склад · Поставщик (накладная подписью) · Статус ·
 * Создано · Позиций · Кол-во. Список до 1024: «RCP-… ● Проведена / Склад ·
 * поставщик / 3 позиции · 12 шт · 26.09.2026».
 */

import { CreatedCell, DocumentNumber, NumberCell } from "./document-cells";
import { positionsCount, quantityTotal, totalsLine } from "./document-totals";
import type { ListColumn } from "../../ui/responsive-list-types";
import { StatusPill } from "../../ui/StatusPill";
import { TwoLineCell } from "../../ui/TwoLineCell";
import type { GoodsReceiptDto } from "../../../lib/api-wms";
import { RECEIPT_STATUS_META } from "../../../lib/shop/status-meta";
import { formatShopDate } from "../../../lib/shop-time";

type Receipt = GoodsReceiptDto;

/** «Основной · ООО Травы» — склад и поставщик одной строкой. */
function placeLine(r: Receipt): string {
  return [r.warehouse?.name ?? "—", r.supplier?.displayName].filter(Boolean).join(" · ");
}

export const RECEIPT_COLUMNS: ListColumn<Receipt>[] = [
  {
    key: "number",
    header: "Номер",
    width: "w-[168px]",
    cell: (r) => <DocumentNumber value={r.receiptNumber} />,
    compactCell: (r) => <span className="font-mono">{r.receiptNumber}</span>,
    compact: "title",
  },
  {
    key: "warehouse",
    header: "Склад",
    width: "w-[160px]",
    cell: (r) => r.warehouse?.name ?? "—",
    fold: { into: "number" },
  },
  {
    key: "supplier",
    header: "Поставщик",
    cell: (r) => (
      <TwoLineCell
        primary={r.supplier?.displayName ?? "—"}
        secondary={r.vendorDocNumber ? `накладная ${r.vendorDocNumber}` : undefined}
      />
    ),
    compact: "subtitle",
    compactCell: placeLine,
  },
  {
    key: "status",
    header: "Статус",
    width: "w-[132px]",
    cell: (r) => <StatusPill meta={RECEIPT_STATUS_META[r.status]} />,
    compact: "trailing",
  },
  {
    key: "created",
    header: "Создано",
    width: "w-[148px]",
    cell: (r) => <CreatedCell at={r.createdAt} by={r.receivedBy?.displayName} />,
    fold: { into: "status", cell: (r) => formatShopDate(r.createdAt) },
  },
  {
    key: "positions",
    header: "Позиций",
    width: "w-[88px]",
    align: "end",
    cell: (r) => <NumberCell value={positionsCount(r.items)} />,
    compact: "meta",
    compactCell: (r) => totalsLine(r.items),
  },
  {
    key: "quantity",
    header: "Кол-во",
    width: "w-[88px]",
    align: "end",
    cell: (r) => <NumberCell value={quantityTotal(r.items)} />,
    fold: { into: "positions", cell: (r) => `${quantityTotal(r.items)} шт` },
  },
  {
    key: "date",
    header: "Дата",
    compactOnly: true,
    cell: (r) => formatShopDate(r.createdAt),
    compact: "meta",
  },
];
